import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import useBranding from "@/hooks/useBranding";

import Home from "@/pages/public/Home";
import Schools from "@/pages/public/Schools";
import SchoolDetail from "@/pages/public/SchoolDetail";
import SchoolsRouteResolver from "@/pages/public/SchoolsRouteResolver";
import SchoolBySlug from "@/pages/public/SchoolBySlug";
import CityPage from "@/pages/public/CityPage";
import Blogs from "@/pages/public/Blogs";
import BlogDetail from "@/pages/public/BlogDetail";
import Events from "@/pages/public/Events";
import ListYourSchool from "@/pages/public/ListYourSchool";
import Login from "@/pages/public/Login";
import ParentPortal from "@/pages/public/ParentPortal";

// sitemap.xml and robots.txt are real static files in frontend/public,
// regenerated by the backend — served directly, no client-side redirect.
import PageView from "@/pages/public/PageView";
import PaymentSuccess from "@/pages/public/PaymentSuccess";
import PaymentCancel from "@/pages/public/PaymentCancel";

import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperSchools from "@/pages/super/SuperSchools";
import SuperBlogs from "@/pages/super/SuperBlogs";
import SuperEvents from "@/pages/super/SuperEvents";
import SuperSubmissions from "@/pages/super/SuperSubmissions";
import SuperSEO from "@/pages/super/SuperSEO";
import SuperSettings from "@/pages/super/SuperSettings";
import SuperUsers from "@/pages/super/SuperUsers";
import SuperPages from "@/pages/super/SuperPages";
import SuperEnquiries from "@/pages/super/SuperEnquiries";
import SuperSchoolForm from "@/pages/super/SuperSchoolForm";

import SchoolDashboard from "@/pages/school/SchoolDashboard";
import Admissions from "@/pages/school/Admissions";
import Students from "@/pages/school/Students";
import Attendance from "@/pages/school/Attendance";
import Exams from "@/pages/school/Exams";
import Fees from "@/pages/school/Fees";
import Messaging from "@/pages/school/Messaging";
import Timetable from "@/pages/school/Timetable";
import SchoolProfile from "@/pages/school/SchoolProfile";
import LeavesTimesheet from "@/pages/school/LeavesTimesheet";
import StaffAndTransport from "@/pages/school/StaffAndTransport";
import Enquiries from "@/pages/school/Enquiries";
import TeacherDashboard from "@/pages/school/TeacherDashboard";
import StudentDashboard from "@/pages/school/StudentDashboard";
import Placeholder from "@/pages/Placeholder";
import ErpHub from "@/pages/erp/ErpHub";
import SessionAttendance from "@/pages/erp/SessionAttendance";
import Transport from "@/pages/erp/Transport";
import Payroll from "@/pages/erp/Payroll";
import ReportsCentre from "@/pages/erp/ReportsCentre";
import Documents from "@/pages/erp/Documents";
import Assets from "@/pages/erp/Assets";
import Library from "@/pages/erp/Library";
import RolesPermissions from "@/pages/erp/RolesPermissions";
import BulkDocsHub from "@/pages/erp/BulkDocsHub";
import StudentProfile from "@/pages/school/StudentProfile";

function BrandingLoader() {
  useBranding();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <BrandingLoader />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/schools" element={<Schools />} />
          <Route path="/schools/:param" element={<SchoolsRouteResolver />} />
          <Route path="/s/:slug" element={<SchoolBySlug />} />
          <Route path="/city/:cityName" element={<CityPage />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/list-your-school" element={<ListYourSchool />} />
          <Route path="/pages/:slug" element={<PageView />} />
          <Route path="/login" element={<Login />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/parent" element={<ProtectedRoute roles={["parent"]}><ParentPortal /></ProtectedRoute>} />

          <Route path="/super" element={<ProtectedRoute roles={["super_admin"]}><SuperDashboard /></ProtectedRoute>} />
          <Route path="/super/schools" element={<ProtectedRoute roles={["super_admin"]}><SuperSchools /></ProtectedRoute>} />
          <Route path="/super/schools/new" element={<ProtectedRoute roles={["super_admin"]}><SuperSchoolForm /></ProtectedRoute>} />
          <Route path="/super/schools/:id/edit" element={<ProtectedRoute roles={["super_admin"]}><SuperSchoolForm /></ProtectedRoute>} />
          <Route path="/super/blogs" element={<ProtectedRoute roles={["super_admin"]}><SuperBlogs /></ProtectedRoute>} />
          <Route path="/super/events" element={<ProtectedRoute roles={["super_admin"]}><SuperEvents /></ProtectedRoute>} />
          <Route path="/super/submissions" element={<ProtectedRoute roles={["super_admin"]}><SuperSubmissions /></ProtectedRoute>} />
          <Route path="/super/seo" element={<ProtectedRoute roles={["super_admin"]}><SuperSEO /></ProtectedRoute>} />
          <Route path="/super/settings" element={<ProtectedRoute roles={["super_admin"]}><SuperSettings /></ProtectedRoute>} />
          <Route path="/super/users" element={<ProtectedRoute roles={["super_admin"]}><SuperUsers /></ProtectedRoute>} />
          <Route path="/super/pages" element={<ProtectedRoute roles={["super_admin"]}><SuperPages /></ProtectedRoute>} />
          <Route path="/super/enquiries" element={<ProtectedRoute roles={["super_admin"]}><SuperEnquiries /></ProtectedRoute>} />

          <Route path="/school" element={<ProtectedRoute roles={["school_admin", "teacher"]}><SchoolDashboard /></ProtectedRoute>} />
          <Route path="/teacher" element={<ProtectedRoute roles={["teacher", "school_admin"]}><TeacherDashboard /></ProtectedRoute>} />
          <Route path="/student" element={<ProtectedRoute roles={["student", "parent"]}><StudentDashboard /></ProtectedRoute>} />
          <Route path="/school/admissions" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Admissions /></ProtectedRoute>} />
          <Route path="/school/enquiries" element={<ProtectedRoute roles={["school_admin", "super_admin"]}><Enquiries /></ProtectedRoute>} />
          <Route path="/school/admissions/:id" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Admissions /></ProtectedRoute>} />
          <Route path="/school/students" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Students /></ProtectedRoute>} />
          <Route path="/school/students/:id" element={<ProtectedRoute roles={["school_admin", "teacher"]}><StudentProfile /></ProtectedRoute>} />
          <Route path="/school/attendance" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Attendance /></ProtectedRoute>} />
          <Route path="/school/exams" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Exams /></ProtectedRoute>} />
          <Route path="/school/fees" element={<ProtectedRoute roles={["school_admin"]}><Fees /></ProtectedRoute>} />
          <Route path="/school/messaging" element={<ProtectedRoute roles={["school_admin"]}><Messaging /></ProtectedRoute>} />
          <Route path="/school/timetable" element={<ProtectedRoute roles={["school_admin", "teacher"]}><Timetable /></ProtectedRoute>} />
          <Route path="/school/leaves" element={<ProtectedRoute roles={["school_admin", "teacher", "parent"]}><LeavesTimesheet /></ProtectedRoute>} />
          <Route path="/school/staff" element={<ProtectedRoute roles={["school_admin"]}><StaffAndTransport /></ProtectedRoute>} />
          <Route path="/school/profile" element={<ProtectedRoute roles={["school_admin"]}><SchoolProfile /></ProtectedRoute>} />

          {/* ERP routes (real pages) */}
          <Route path="/erp/sessions" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/subjects" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/homework" element={<ProtectedRoute roles={["school_admin","super_admin","teacher","parent","student"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/notices" element={<ProtectedRoute roles={["school_admin","super_admin","teacher","parent","student"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/communications" element={<ProtectedRoute roles={["school_admin","super_admin"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/audit" element={<ProtectedRoute roles={["school_admin","super_admin"]}><ErpHub /></ProtectedRoute>} />
          <Route path="/erp/session-attendance" element={<ProtectedRoute roles={["school_admin","super_admin","teacher","parent","student"]}><SessionAttendance /></ProtectedRoute>} />
          <Route path="/erp/transport" element={<ProtectedRoute roles={["school_admin","super_admin"]}><Transport /></ProtectedRoute>} />
          <Route path="/erp/pickup" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><Transport /></ProtectedRoute>} />

          {/* Scaffolded — placeholder pages (backend endpoints ready, UI shipping next sprint) */}
          <Route path="/erp/referrals" element={<ProtectedRoute roles={["school_admin","super_admin"]}><Placeholder title="Admission Referrals" description="Referral tracking, coupons and payouts — schema is ready via /api/fee/coupons and /api/erp/*." /></ProtectedRoute>} />
          <Route path="/erp/documents" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><Documents /></ProtectedRoute>} />
          <Route path="/erp/marks" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><Placeholder title="Marks Entry" description="Grade auto-calculated on submit. Backend live: POST /api/erp/marks {exam_id, student_id, theory, practical}." /></ProtectedRoute>} />
          <Route path="/erp/admit-cards" element={<ProtectedRoute roles={["school_admin","super_admin"]}><BulkDocsHub kind="admit-cards" /></ProtectedRoute>} />
          <Route path="/erp/report-cards" element={<ProtectedRoute roles={["school_admin","super_admin"]}><BulkDocsHub kind="report-cards" /></ProtectedRoute>} />
          <Route path="/erp/certificates" element={<ProtectedRoute roles={["school_admin","super_admin"]}><BulkDocsHub kind="certificates" /></ProtectedRoute>} />
          <Route path="/erp/id-cards" element={<ProtectedRoute roles={["school_admin","super_admin"]}><BulkDocsHub kind="id-cards" /></ProtectedRoute>} />
          <Route path="/erp/tc" element={<ProtectedRoute roles={["school_admin","super_admin"]}><BulkDocsHub kind="tc" /></ProtectedRoute>} />
          <Route path="/erp/library" element={<ProtectedRoute roles={["school_admin","super_admin","teacher"]}><Library /></ProtectedRoute>} />
          <Route path="/erp/assets" element={<ProtectedRoute roles={["school_admin","super_admin"]}><Assets /></ProtectedRoute>} />
          <Route path="/erp/payroll" element={<ProtectedRoute roles={["school_admin","super_admin"]}><Payroll /></ProtectedRoute>} />
          <Route path="/erp/reports" element={<ProtectedRoute roles={["school_admin","super_admin"]}><ReportsCentre /></ProtectedRoute>} />
          <Route path="/erp/roles" element={<ProtectedRoute roles={["school_admin","super_admin"]}><RolesPermissions /></ProtectedRoute>} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
